import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Popover, Select } from 'antd';
import { getProjectColourPalette } from '../../../../js/helper';
import useCloseOnScroll from '../../../../hooks/useCloseOnScroll';

const ConnectedProjectSelect = ({
  me,
  callback,
  currentValue,
  workspaceProjects,
  projects,
  children
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { 1: selectedProjectId } = window.location.pathname.split("/");

  const workspaceProjectsOptions = useMemo(() => {
    const selectedProjectsIdList = projects.map(prj => prj?.projectId || prj?._id);
    const publicProjects = workspaceProjects?.filter(p => p?.access == 'public' || selectedProjectsIdList?.includes(p?._id));
    return publicProjects;
  }, [projects, workspaceProjects]);

  const [options, setOptions] = useState(workspaceProjectsOptions);
  const [search, setSearch] = useState('');
  const [selectedValue, setSelectedValue] = useState();
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const selectRef = useRef(null);
  const triggerRef = useRef(null);

  useCloseOnScroll(popoverOpen, triggerRef, () => setPopoverOpen(false));

  const currentProjectIds = currentValue?.map(p => p?.projectId);

  useEffect(() => {
    setOptions(workspaceProjectsOptions);
  }, [workspaceProjects])

  useEffect(() => {
    // Reset highlight when options change
    setHighlightedIndex(0);
  }, [options]);

  const handleSubmit = useCallback(() => {
    if (selectedValue?._id && callback) {
      callback(selectedValue);
    }
    setSelectedValue([]);
  }, [selectedValue, callback]);

  const optionRefs = useRef([]);
  useEffect(() => {
    if (optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, options]);

  const ProjectOption = ({ option, index }) => {
    const inProject = currentProjectIds?.includes(option?._id);
    const inSelected = selectedValue?._id == option?._id;
    const isSelected = inProject || inSelected;
    const isHighlighted = index === highlightedIndex;
    const palette = getProjectColourPalette(option?.metadata?.colour);
    return (
      <div
        key={option?._id}
        ref={el => optionRefs.current[index] = el}
        onClick={(e) => {
          e.stopPropagation();
          if (inProject) return;
          setSelectedValue({ label: option?.title, value: option?._id, ...option });
          setHighlightedIndex(index);
          setSearch('');
          if (selectRef.current && selectRef.current.focus) {
            setTimeout(() => selectRef.current.focus(), 0);
          }
        }}
        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors"
        style={{
          background: isHighlighted ? 'rgba(99,102,241,0.08)' : isSelected ? 'rgba(99,102,241,0.06)' : '',
        }}
        onMouseEnter={e => { if (!isHighlighted) e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = isHighlighted ? 'rgba(99,102,241,0.08)' : isSelected ? 'rgba(99,102,241,0.06)' : ''; }}
        tabIndex={-1}
      >
        <div
          className="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-lg"
          style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
        >
          <img src='/images/icons/board.svg' className="w-4 h-4" style={{ filter: 'brightness(0) saturate(100%)', opacity: 0.55 }} />
        </div>
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="min-w-0">
            <div className="text-[13px] font-medium truncate" style={{ color: 'rgba(15,15,25,0.82)' }}>{option?.title}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span
                className="text-[10px] px-1.5 py-0.5 rounded capitalize font-medium"
                style={option?.access === 'public'
                  ? { background: 'rgba(16,185,129,0.1)', color: '#059669' }
                  : { background: 'rgba(99,102,241,0.08)', color: 'rgba(99,102,241,0.7)' }}
              >
                {option?.access}
              </span>
              {option?.isParticipant && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'rgba(99,102,241,0.1)', color: '#4f46e5' }}>Joined</span>
              )}
              {option?.archived && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'rgba(15,15,25,0.07)', color: 'rgba(15,15,25,0.4)' }}>Archived</span>
              )}
            </div>
          </div>
          {isSelected && (
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="#4f46e5">
              <path d="m2.457 8.516.969-.99 2.516 2.481 5.324-5.304.985.989-6.309 6.284z" />
            </svg>
          )}
        </div>
      </div>
    );
  }

  const dropdownRenderer = useCallback(() => (
    <div
      className={`assignee-dropdown-content-div w-[300px]`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1));
          e.preventDefault();
        } else if (e.key === 'ArrowUp') {
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          e.preventDefault();
        } else if (e.key === 'Enter') {
          if (options[highlightedIndex]) {
            setSelectedValue({
              label: options[highlightedIndex]?.title,
              value: options[highlightedIndex]?._id,
              ...options[highlightedIndex]
            });
            setSearch('');
            if (selectRef.current) {
              selectRef.current.blur();
              setTimeout(() => selectRef.current.focus(), 0);
            }
          }
          e.preventDefault();
        }
      }}
    >
      <div className="overflow-hidden dropdown-content">
        <div className="p-3">
          <Select
            ref={selectRef}
            mode="multiple"
            style={{ width: '100%' }}
            placeholder='Search projects...'
            dropdownRender={() => null} // completely hide the dropdown
            suffixIcon={null} // hides the dropdown icon
            onClick={(e) => e.stopPropagation()}
            onChange={(input) => {
              setSelectedValue(input);
              if (!input || (Array.isArray(input) && input.length === 0)) {
                setOptions(workspaceProjectsOptions);
                setSearch('');
                setHighlightedIndex(0);
              }
            }}
            onSearch={(input) => {
              const newProjects = workspaceProjectsOptions?.filter(opt => {
                const label = opt?.title?.toLowerCase();
                return label?.includes(input?.toLowerCase());
              });
              setOptions(newProjects);
              setSearch(input);
              setHighlightedIndex(0);
            }}
            options={options}
            value={selectedValue}
            autoFocus
          />
          <div className="overflow-y-scroll w-full max-h-80 mt-2">
            {options?.length == 0 ?
              <div className='flex align-center justify-center bg-slate-100 p-2'>
                No projects found
              </div>
              : search ? <div
                onClick={(e) => {
                  e.stopPropagation();
                  setOptions(workspaceProjectsOptions);
                  setSearch('');
                  setHighlightedIndex(0);
                }}
                className="flex align-center pl-2 py-2 hover:bg-slate-200 w-full cursor-pointer"
              >
                <div className="card-image bg-indigo-200 h-10 w-10 flex align-center justify-center font-16">
                  X
                </div>
                <div className="ml-3">
                  <div className="font-16 font-bold">Clear</div>
                </div>
              </div> : <></>
            }
            {options?.map((option, i) => <ProjectOption option={option} index={i} />)}
          </div>
        </div>
      </div>
    </div>
  ), [selectedValue, options, highlightedIndex])

  return (
    <div ref={triggerRef} className='assignee-popover'>
      <Popover
        onClick={(e) => e.stopPropagation()}
        content={dropdownRenderer}
        placement="bottomLeft"
        open={popoverOpen}
        onOpenChange={(open) => {
          setPopoverOpen(open);
          if (open) {
            setHighlightedIndex(0);
            setTimeout(() => {
              if (selectRef.current && selectRef.current.focus) {
                selectRef.current.focus();
              }
            }, 0);
          }
          if (!open) {
            handleSubmit();
          }
        }}
        arrow={false}
        trigger="click">
        <>
          {children}
        </>
      </Popover>
    </div>
  );
};

const makeMapStateToProps = () => {
  return (state, props) => {
    return ({
      me: state.userReducer.user,
      workspaceProjects: state.workspaceReducer.workspaceProjects,
      projects: state.projectReducer.myProjects,
    });
  }
};

export default connect(makeMapStateToProps)(ConnectedProjectSelect);
